import React, { useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import classes from '../styles.module.scss';
import { SpeechSegment } from '../../../services/api.service';
import { langLabel, resolveSpeakerLang } from '../utils/speaker-language';
import { getSpeakerName } from '../utils/audio-segments';

const cn = createCn('file', classes);

export type SpeakerLangCode = '' | 'ru' | 'kz' | 'en';

export const SPEAKER_LANG_OPTIONS: { value: SpeakerLangCode; label: string }[] = [
  { value: '', label: 'Авто (multi)' },
  { value: 'ru', label: 'Русский (ru)' },
  { value: 'kz', label: 'Қазақша (kz)' },
  { value: 'en', label: 'English (en)' },
];

export type SpeakerGenderCode = '' | 'male' | 'female';

export const SPEAKER_GENDER_OPTIONS: { value: SpeakerGenderCode; label: string }[] = [
  { value: '', label: 'Авто' },
  { value: 'male', label: 'Мужской' },
  { value: 'female', label: 'Женский' },
];

type SpeakerRow = {
  key: string;
  label: string;
  displayName: string;
  language: SpeakerLangCode;
  gender: SpeakerGenderCode;
  segmentCount: number;
  kzSignalPct: number;
};

type Props = {
  segments: SpeechSegment[];
  pipeline?: string | null;
  initialLanguages?: Record<string, string>;
  initialGenders?: Record<string, { gender?: string; display?: string }>;
  onSave: (languages: Record<string, string>, genders: Record<string, string>) => Promise<void>;
  onRetranscribe: () => Promise<void>;
  onAutoDetect?: () => Promise<void>;
  saving?: boolean;
  retranscribing?: boolean;
  detecting?: boolean;
};

function resolveGenderCode(
  key: string,
  display: string,
  initial: Record<string, { gender?: string; display?: string }>,
  segGender?: string,
): SpeakerGenderCode {
  const raw = initial[key]?.gender || initial[display]?.gender;
  if (raw === 'male' || raw === 'female') return raw;
  if (segGender === 'male' || segGender === 'female') return segGender;
  if (segGender === 'Мужской') return 'male';
  if (segGender === 'Женский') return 'female';
  return '';
}

function buildSpeakerRows(
  segments: SpeechSegment[],
  initial: Record<string, string>,
  initialGenders: Record<string, { gender?: string; display?: string }>,
): SpeakerRow[] {
  const stats = new Map<string, { row: SpeakerRow; kzHits: number }>();

  segments.forEach(seg => {
    const label = seg.speaker_label || seg.speaker_display || seg.speaker_name || 'Unknown';
    const display = getSpeakerName(seg);
    const key = display;

    if (!stats.has(key)) {
      const langRaw = (
        initial[key]
        || initial[display]
        || initial[label]
        || (seg.speaker_label ? initial[seg.speaker_label] : undefined)
        || seg.speaker_language
        || ''
      );
      const language = (['ru', 'kz', 'en'].includes(langRaw) ? langRaw : '') as SpeakerLangCode;
      stats.set(key, {
        row: {
          key,
          label,
          displayName: display,
          language,
          gender: resolveGenderCode(key, display, initialGenders, seg.speaker_gender),
          segmentCount: 0,
          kzSignalPct: 0,
        },
        kzHits: 0,
      });
    }
    const rec = stats.get(key)!;
    rec.row.segmentCount += 1;
    const det = seg.detected_language || '';
    if (det === 'kz' || det === 'mixed') rec.kzHits += 1;
  });

  return Array.from(stats.values()).map(({ row, kzHits }) => ({
    ...row,
    kzSignalPct: row.segmentCount > 0 ? Math.round((kzHits / row.segmentCount) * 100) : 0,
  }));
}

const SpeakerLanguagePanel = ({
  segments,
  pipeline = null,
  initialLanguages = {},
  initialGenders = {},
  onSave,
  onRetranscribe,
  onAutoDetect,
  saving = false,
  retranscribing = false,
  detecting = false,
}: Props) => {
  const initialRows = useMemo(
    () => buildSpeakerRows(segments, initialLanguages, initialGenders),
    [segments, initialLanguages, initialGenders],
  );
  const [rows, setRows] = useState<SpeakerRow[]>(initialRows);

  React.useEffect(() => {
    setRows(buildSpeakerRows(segments, initialLanguages, initialGenders));
  }, [segments, initialLanguages, initialGenders]);

  if (!rows.length) return null;

  const applyKazakhSuggestions = () => {
    setRows(prev => prev.map(r => {
      const name = r.displayName.toLowerCase();
      if (/нурал|алихан|батыр|абдых|nural|alikhan/.test(name)) {
        return { ...r, language: 'kz' as SpeakerLangCode };
      }
      if (r.kzSignalPct >= 12 || (r.segmentCount >= 20 && r.kzSignalPct >= 5)) {
        return { ...r, language: 'kz' as SpeakerLangCode };
      }
      if (/галихан|лаура|кайрат|мухамед/.test(name)) {
        return { ...r, language: 'ru' as SpeakerLangCode };
      }
      return r;
    }));
  };

  const setLang = (key: string, language: SpeakerLangCode) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, language } : r)));
  };

  const setGender = (key: string, gender: SpeakerGenderCode) => {
    setRows(prev => prev.map(r => (r.key === key ? { ...r, gender } : r)));
  };

  const buildLangPayload = (): Record<string, string> => {
    const out: Record<string, string> = {};
    rows.forEach(r => {
      if (r.language) {
        out[r.key] = r.language;
        if (r.displayName !== r.key) out[r.displayName] = r.language;
      }
    });
    return out;
  };

  const buildGenderPayload = (): Record<string, string> => {
    const out: Record<string, string> = {};
    rows.forEach(r => {
      if (r.gender) out[r.key] = r.gender;
    });
    return out;
  };

  const hasExplicitLang = rows.some(r => r.language);
  const hasExplicitGender = rows.some(r => r.gender);
  const allowRetranscribe = true;

  return (
    <div className={cn('speaker-lang-panel')}>
      <div className={cn('speaker-lang-header')}>
        <h3 className={cn('speaker-lang-title')}>Язык и пол спикеров</h3>
        <p className={cn('speaker-lang-hint')}>
          Язык и пол — метаданные для протокола: влияют на формулировки («он/она») и объём §3 «Выступления».
          Источник текста — реплики в транскрипте ниже. Для большего числа спикеров — кнопка
          «Больше спикеров (Pyannote)» в блоке выше. Ретранскрибация — только текст, без смены спикеров.
        </p>
      </div>
      <div className={cn('speaker-lang-rows')}>
        {rows.map(row => (
          <div key={row.key} className={cn('speaker-lang-row')}>
            <div className={cn('speaker-lang-col-label')}>
              <span className={cn('speaker-lang-name')}>{row.displayName}</span>
              {row.label !== row.displayName && (
                <span className={cn('speaker-lang-id')}>{row.label}</span>
              )}
              <span className={cn('speaker-lang-meta')}>
                {row.segmentCount} репл.
                {row.kzSignalPct > 0 && ` · kz-сигнал ${row.kzSignalPct}%`}
              </span>
              {row.language && (
                <span className={cn('speaker-lang-badge')}>{langLabel(row.language)}</span>
              )}
            </div>
            <select
              className={cn('speaker-lang-select')}
              value={row.language}
              onChange={e => setLang(row.key, e.target.value as SpeakerLangCode)}
            >
              {SPEAKER_LANG_OPTIONS.map(opt => (
                <option key={opt.value || 'auto'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <select
              className={cn('speaker-lang-select')}
              value={row.gender}
              onChange={e => setGender(row.key, e.target.value as SpeakerGenderCode)}
              title="Пол для протокола"
            >
              {SPEAKER_GENDER_OPTIONS.map(opt => (
                <option key={opt.value || 'auto'} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        ))}
      </div>
      <div className={cn('speaker-lang-actions')}>
        {onAutoDetect && (
          <button
            type="button"
            className={cn('download-btn', { secondary: true })}
            disabled={detecting}
            onClick={onAutoDetect}
          >
            {detecting ? 'Определение…' : 'Определить автоматически'}
          </button>
        )}
        <button
          type="button"
          className={cn('download-btn', { secondary: true })}
          onClick={applyKazakhSuggestions}
        >
          Казахоязычные → kz
        </button>
        <button
          type="button"
          className={cn('download-btn', { secondary: true })}
          disabled={saving || (!hasExplicitLang && !hasExplicitGender)}
          onClick={() => onSave(buildLangPayload(), buildGenderPayload())}
        >
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
        {allowRetranscribe && (
          <button
            type="button"
            className={cn('download-btn')}
            disabled={retranscribing || !hasExplicitLang}
            onClick={onRetranscribe}
          >
            {retranscribing ? 'Перетранскрибация…' : 'Перетранскрибировать'}
          </button>
        )}
      </div>
    </div>
  );
};

export default SpeakerLanguagePanel;
